import { useEffect, useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../auth/AuthProvider'
import { effectiveStatus, type StatusTimes } from '../lib/status'

/**
 * Profilsidan: visningsnamn och smeknamn.
 *
 * Smeknamnet är det enda som visas i den publika tabellen. Därför sätts det
 * innan man kommer vidare — en tom kolumn hade betytt att någon står som
 * "Deltagare a1b2" på en sida vem som helst kan öppna.
 *
 * Visningsnamnet byts numera av deltagaren själv — poolen växer med folk
 * admin inte känner, och namnbyten kan inte gå via honom. Under pågående
 * tävling är båda namnen frysta: databasens guard_profile_names avvisar
 * bytet, och formuläret säger det i förväg i stället för att låta någon
 * skriva klart och först då få nej. Första smeknamnet går alltid att sätta,
 * även under tävling — annars står en ny deltagare fast i grinden.
 *
 * Skiftlägesokänslig unikhet ligger som index i databasen. Frontend gissar
 * inte utan låter felkoden avgöra, annars kan de två glida isär.
 */
export function Nickname({ onDone }: { onDone?: () => void }) {
  const { profile, refreshProfile } = useAuth()
  const [value, setValue] = useState(profile?.nickname ?? '')
  const [displayName, setDisplayName] = useState(profile?.display_name ?? '')
  const [frozen, setFrozen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [saved, setSaved] = useState(false)

  const firstNickname = !profile?.nickname

  useEffect(() => {
    // Samma fönster som databasens name_change_frozen(): från inlämnings-
    // stopp (klockan, inte statusen) tills tävlingen avslutats.
    supabase
      .from('tournaments')
      .select('status, lock_time, starts_at')
      .in('status', ['open', 'locked', 'live'])
      .then(({ data }) => {
        const active = ((data ?? []) as StatusTimes[]).some((t) => {
          const s = effectiveStatus(t)
          return s === 'locked' || s === 'live'
        })
        setFrozen(active)
      })
  }, [])

  const trimmedNick = value.trim()
  const trimmedName = displayName.trim()
  const nickTooShort = trimmedNick.length > 0 && trimmedNick.length < 2
  const nickTooLong = trimmedNick.length > 24
  const nameTooShort = trimmedName.length < 2

  const nameChanged = trimmedName !== (profile?.display_name ?? '')
  const nickChanged = trimmedNick !== (profile?.nickname ?? '')
  // Under frys får bara första smeknamnet sättas — och då utan namnbyte.
  const blocked = frozen && !firstNickname && (nameChanged || nickChanged)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!profile) return
    setBusy(true)
    setError(null)
    setSaved(false)

    const patch: { nickname: string; display_name?: string } = { nickname: trimmedNick }
    // Vid första smeknamnet under frys skickas display_name inte med alls —
    // guardens undantag gäller bara när visningsnamnet lämnas orört.
    if (!frozen || !firstNickname) patch.display_name = trimmedName

    const { error } = await supabase
      .from('profiles')
      .update(patch)
      .eq('id', profile.id)

    setBusy(false)

    if (error) {
      // 23505 = unique_violation, 23514 = guard-frysningen.
      setError(
        error.code === '23505'
          ? 'Det smeknamnet är upptaget. Välj ett annat.'
          : error.code === '23514'
            ? 'Namn kan inte ändras medan en tävling pågår. Vänta tills den är avslutad.'
            : error.message,
      )
      return
    }

    await refreshProfile()
    setSaved(true)
    onDone?.()
  }

  return (
    <div className="page" style={{ maxWidth: 460 }}>
      <div className="stack">
        <div>
          <p className="eyebrow">{firstNickname ? 'Nästan klart' : 'Profil'}</p>
          <h1>{firstNickname ? 'Välj ditt smeknamn' : 'Ditt namn'}</h1>
        </div>

        <form className="card stack" onSubmit={onSubmit}>
          <p className="muted">
            Resultattabellen går att öppna utan att logga in, så att man kan skicka
            en länk till den. Där visas bara smeknamnet — ditt visningsnamn syns
            bara för den som är inloggad.
          </p>

          {frozen && !firstNickname && (
            <div className="notice">
              En tävling är igång — namn kan bytas igen när den är avslutad.
            </div>
          )}

          <div>
            <label htmlFor="displayname">Visningsnamn</label>
            <input
              id="displayname"
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); setSaved(false) }}
              maxLength={60}
              required
              disabled={frozen && firstNickname}
              placeholder="Förnamn Efternamn"
            />
            <p className="muted">Namnet de andra i poolen ser på leaderboarden.</p>
          </div>

          <div>
            <label htmlFor="nickname">Smeknamn</label>
            <input
              id="nickname"
              value={value}
              onChange={(e) => { setValue(e.target.value); setSaved(false) }}
              maxLength={24}
              required
              autoFocus={firstNickname}
              placeholder="t.ex. Fjakel"
            />
          </div>

          {nickTooShort && <p className="muted">Minst två tecken.</p>}
          {nickTooLong && <p className="muted">Högst 24 tecken.</p>}
          {error && <div className="notice notice--error">{error}</div>}
          {saved && <div className="notice notice--ok">Sparat.</div>}

          <button
            className="primary"
            type="submit"
            disabled={busy || trimmedNick.length < 2 || nickTooLong || nameTooShort || blocked}
          >
            {busy ? 'Sparar…' : 'Spara'}
          </button>

          <p className="muted">
            Du kan byta båda senare — dock inte medan en tävling pågår. Väljer du
            ditt eget namn som smeknamn syns det förstås publikt — det är hela
            skillnaden mot att välja något annat.
          </p>
        </form>
      </div>
    </div>
  )
}
